import React, { useEffect, useMemo, useState } from "react";
import axios, { AxiosProgressEvent } from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";

import {
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import Breadcrumb from "Common/BreadCrumb";
import { Link, useNavigate, useParams } from "react-router-dom";
import Dropzone from "react-dropzone";
import ReactDOM from "react-dom";
import {
  addProduct,
  downloadTemplateCSV,
  exportProductsToCSV,
  fetchAllActiveCategories,
  fetchBasePrice,
  getAllAttributeSets,
  getAttributeSetById,
  getProductById,
  importProductsFromCSV,
  updateProductById,
} from "api/apis";
import { Category, VariableOptionAttribute } from "interface";

interface Attribute {
  _id: string;
  label: string;
  type: "string" | "number" | "boolean" | "options";
  options: string[];
  isVariable: boolean;
  step?: string;
  __v?: number;
  isCharge?: boolean;
  valueType?: "fixed" | "percentage";
}

interface AttributeSet {
  _id: string;
  name: string;
  attributes: Attribute[];
  __v: number;
}

interface VariantAttribute {
  attrId?: string; // Make optional since not all uses need it
  label: string;
  value: string | number | boolean;
  type: string;
  isVariable?: boolean;
  _id?: string;
  valueType?: string;
  QTY?: number;
  price?: number;
  show?: boolean;
}

interface VariantVariableValue {
  value: string | number;
  valueType?: string;
  show?: boolean;
}

interface VariableOptionValue {
  selectedOption: string; // Stores the selected option ("Black", "Gold")
  inputValue: string | number; // Stores the custom input value
  type: "text" | "number";
  price: number;
  QTY?: number;
}

interface VariantPrice {
  combo: VariantAttribute[];
  sku?: string;
  price: number;
  quantity: number;
  variableValues: Record<string, VariantVariableValue>;
  variableOptions?: Record<string, VariableOptionValue[]>; // Make optional with ?
  attributes?: VariantAttribute[];
}

interface VariantImage {
  path: string;
  file?: File;
  id: string; // Ensure this is in your interface
  isExisting: boolean;
  markedForDeletion?: boolean;
}

interface ProductVariant {
  sku?: string;
  price: number;
  quantity: number;
  attributes: VariantAttribute[];
  variableOptions?: Record<string, VariableOptionValue[]>;
}

interface VariantAttribute {
  label: string;
  value: string | number | boolean;
  type: string;
  isVariable?: boolean;
  valueType?: string;
}
interface StableImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}
function CreateProduct() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"simple" | "customizable">(
    "simple"
  );
  const [selectedSetAttributes, setSelectedSetAttributes] = useState<
    Attribute[]
  >([]);

  const [attributeSets, setAttributeSets] = useState<AttributeSet[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string>("");
  const [productName, setProductName] = useState<string>("");
  // Update your state declarations
  const [sku, setSku] = useState<string>("");
  const [attributeValues, setAttributeValues] = useState<
    Record<string, (string | number | boolean)[]>
  >({});
  const [variantPrices, setVariantPrices] = useState<VariantPrice[]>([]);
  const [variantImages, setVariantImages] = useState<VariantImage[][]>([]);
  const [basePrice24K, setBasePrice24K] = useState<string>("");
  const [karatMap, setKaratMap] = useState<Record<string, number>>({});
  const [currentBasePrice, setCurrentBasePrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariableOptions, setSelectedVariableOptions] = useState<
    Record<string, Record<string, string[]>> // Now stores arrays of selected values
  >({});
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [productImages, setProductImages] = useState<File[]>([]);
  const [existingProductImages, setExistingProductImages] = useState<string[]>(
    []
  );
  // In your component state, add:
  const [primaryImages, setPrimaryImages] = useState<File[]>([]);
  const [existingPrimaryImages, setExistingPrimaryImages] = useState<string[]>(
    []
  );
  // Add this state near your other state declarations
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState<string>(
    "Product created successfully!"
  );
  const [isTaxable, setIsTaxable] = useState<boolean>(true);
  const [isNewArrival, setIsNewArrival] = useState<boolean>(false);
  const [isFeatured, setIsFeatured] = useState<boolean>(false);
  const [showAttribute, setShowAttribute] = useState(true); // State for toggle
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    errors?: any[];
  } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Add these near your other state declarations
  const [productVideo, setProductVideo] = useState<File | null>(null);
  const [existingProductVideo, setExistingProductVideo] = useState<
    string | null
  >(null);
  const [variantVideos, setVariantVideos] = useState<(File | null)[]>([]);
  const [existingVariantVideos, setExistingVariantVideos] = useState<string[]>(
    []
  );

  const [dynamicProductVideo, setDynamicProductVideo] = useState<File | null>(
    null
  );
  const [existingDynamicProductVideo, setExistingDynamicProductVideo] =
    useState<string | null>(null);

  const variantImagesMemo = useMemo(() => variantImages, [variantImages]);
  const getSafeValue = (
    value: string | number | boolean | undefined
  ): string | number | undefined => {
    return typeof value === "boolean" ? undefined : value;
  };

  const categoryOptions = categories.map((cat) => ({
    label: cat.name,
    value: cat._id,
  }));

  const StableImage: React.FC<StableImageProps> = ({ src, alt, ...props }) => {
    const [currentSrc, setCurrentSrc] = useState(src);

    useEffect(() => {
      setCurrentSrc(src);
    }, [src]);

    return <img src={currentSrc} alt={alt} {...props} />;
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await fetchAllActiveCategories();
        setCategories(data);
        // toast.success("Categories loaded successfully");
      } catch (error) {
        console.error("Error fetching categories", error);
        toast.error("Failed to load categories");
      }
    };

    fetchCategories();
  }, []);
  // Fetch initial data

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchProductData(id);
    }
  }, [id]);

  useEffect(() => {
    console.log("Current Variant Prices:", variantPrices);
    console.log("Selected Attributes:", selectedSetAttributes);
  }, [variantPrices, selectedSetAttributes]);

  useEffect(() => {
    console.log("Current Variants:", variants);
    console.log("Variant Prices:", variantPrices);
  }, [variants, variantPrices]);

  // const fetchProductData = async (productId: string) => {
  //   try {
  //     setIsLoading(true);
  //     const response = await getProductById(productId);
  //     console.log("Proper response", response);
  //     const product = response.product;

  //     const productImages = Array.isArray(response.product.images)
  //       ? response.product.images
  //       : response.product.images
  //       ? [response.product.images]
  //       : [];

  //     setExistingProductImages(productImages);
  //     // Set basic info
  //     setProductName(product.name || "");
  //     setSku(product.sku || "");
  //     setActiveTab(product.isSimpleProduct ? "simple" : "customizable");
  //     setIsTaxable(product.isTaxable ?? product.taxable ?? false);
  //     setIsNewArrival(product.isNewArrival ?? false);
  //     setIsFeatured(product.isFeatured ?? false);
  //     // In fetchProductData function
  //     if (product.category) {
  //       setSelectedCategoryId(
  //         typeof product.category === "object"
  //           ? product.category._id
  //           : product.category
  //       );
  //     }
  //     // Handle attribute set
  //     const attributeSetId =
  //       typeof product.attributeSet === "object"
  //         ? product.attributeSet._id
  //         : product.attributeSet;

  //     if (!attributeSetId) {
  //       console.warn("No attribute set found for product");
  //       setIsLoading(false);
  //       return;
  //     }

  //     // Fetch attribute set
  //     const attributeSet = await getAttributeSetById(attributeSetId);
  //     if (!attributeSet) {
  //       console.error("Failed to load attribute set");
  //       setIsLoading(false);
  //       return;
  //     }

  //     // Process variants if it's customizable
  //     let variantPrices: VariantPrice[] = [];
  //     let variantImages: VariantImage[][] = [];
  //     let initialAttributeValues: Record<
  //       string,
  //       (string | number | boolean)[]
  //     > = {};

  //     if (!product.isSimpleProduct && response.allVariants) {
  //       // Get all option attributes that are used for variants (type: "options" and isVariable: false)
  //       const optionAttributes = attributeSet.attributes.filter(
  //         (attr) => attr.type === "options" && !attr.isVariable
  //       );

  //       // Initialize attribute values map for variant-defining attributes
  //       optionAttributes.forEach((attr) => {
  //         initialAttributeValues[attr._id] = [];
  //       });

  //       // Process each variant to extract attribute values
  //       response.allVariants.forEach((variant) => {
  //         variant.attributes.forEach((attr) => {
  //           const matchingAttr = optionAttributes.find(
  //             (a) => a.label === attr.label
  //           );
  //           if (
  //             matchingAttr &&
  //             !initialAttributeValues[matchingAttr._id].includes(attr.value)
  //           ) {
  //             initialAttributeValues[matchingAttr._id].push(attr.value);
  //           }
  //         });
  //       });

  //       // Now create variant combinations based on these values
  //       variantPrices = generateCombinations(
  //         optionAttributes,
  //         initialAttributeValues
  //       );

  //       // Map existing variant data to the generated combinations
  //       variantPrices = variantPrices.map((newVariant) => {
  //         const existingVariant = response.allVariants?.find((variant) => {
  //           return newVariant.combo.every((newAttr) => {
  //             return variant.attributes.some(
  //               (existingAttr) =>
  //                 existingAttr.label === newAttr.label &&
  //                 existingAttr.value === newAttr.value
  //             );
  //           });
  //         });

  //         if (existingVariant) {
  //           // Create a mapping of all variable attributes (non-variant-defining)
  //           const variableValues: Record<string, VariantVariableValue> = {};
  //           const variableOptions: Record<string, VariableOptionValue[]> = {};

  //           // Process all attributes from the existing variant
  //           existingVariant.attributes.forEach((attr) => {
  //             const matchingAttr = attributeSet.attributes.find(
  //               (a) => a.label === attr.label
  //             );

  //             if (!matchingAttr) return;

  //             // If it's a variable option attribute (like "Beats")
  //             if (matchingAttr.isVariable && matchingAttr.type === "options") {
  //               if (!variableOptions[matchingAttr._id]) {
  //                 variableOptions[matchingAttr._id] = [];
  //               }

  //               // Type assertion to access optional properties
  //               const attrWithExtras = attr as VariantAttribute;

  //               variableOptions[matchingAttr._id].push({
  //                 selectedOption: attr.value.toString(),
  //                 inputValue: attrWithExtras.QTY || 0,
  //                 type: "number",
  //                 price: attrWithExtras.price || 0,
  //                 QTY: attrWithExtras.QTY || 0,
  //               });
  //             }
  //             // If it's a regular variable attribute (not variant-defining)
  //             else if (matchingAttr.isVariable) {
  //               const attrWithExtras = attr as VariantAttribute;
  //               variableValues[matchingAttr._id] = {
  //                 value: attr.value,
  //                 valueType: attrWithExtras.valueType || "fixed",
  //                 show: attrWithExtras.show !== false,
  //               };
  //             }
  //           });

  //           return {
  //             ...newVariant,
  //             price: existingVariant.price,
  //             quantity: existingVariant.quantity,
  //             sku: existingVariant.sku,
  //             variableValues,
  //             variableOptions,
  //           };
  //         }
  //         return newVariant;
  //       });

  //       // Initialize variant images
  //       // In the variant images initialization:
  //       variantImages = response.allVariants.map((variant) =>
  //         variant.images.map((image) => ({
  //           path: image,
  //           id: `existing-${image}`, // Stable ID for existing images
  //           isExisting: true,
  //         }))
  //       );
  //     }

  //     // Update state
  //     ReactDOM.unstable_batchedUpdates(() => {
  //       setSelectedSetId(attributeSetId);
  //       setSelectedSetAttributes(attributeSet.attributes);
  //       setAttributeValues(initialAttributeValues);
  //       setVariantPrices(variantPrices);
  //       setVariantImages(variantImages);
  //       setExistingProductImages(product.mainImages || []);
  //     });
  //   } catch (error) {
  //     console.error("Failed to fetch product", error);
  //     setError("Failed to load product data");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const fetchProductData = async (productId: string) => {
    try {
      setIsLoading(true);
      const response = await getProductById(productId);
      console.log("Proper response", response);
      const product = response.product;

      // Handle main product images
      const productImages = Array.isArray(response.product.images)
        ? response.product.images
        : response.product.images
        ? [response.product.images]
        : [];

      setExistingProductImages(productImages);

      // Set basic info
      setProductName(product.name || "");
      setSku(product.sku || "");
      setActiveTab(product.isSimpleProduct ? "simple" : "customizable");
      setIsTaxable(product.isTaxable ?? product.taxable ?? false);
      setIsNewArrival(product.isNewArrival ?? false);
      setIsFeatured(product.isFeatured ?? false);
      setCurrentBasePrice(product.currentBasePrice || 0);

      // Handle category
      if (product.category) {
        const categoryIds = Array.isArray(product.category)
          ? product.category.map((cat) =>
              typeof cat === "object" ? cat._id : cat
            )
          : [
              typeof product.category === "object"
                ? product.category._id
                : product.category,
            ];

        setSelectedCategoryIds(categoryIds);
      }

      if (product.video) {
        setExistingProductVideo(product.video);
      }

      if (product.video && !product.isSimpleProduct) {
        setExistingDynamicProductVideo(product.video);
      }

      // Handle attribute set
      const attributeSetId =
        typeof product.attributeSet === "object"
          ? product.attributeSet._id
          : product.attributeSet;

      if (!attributeSetId) {
        console.warn("No attribute set found for product");
        setIsLoading(false);
        return;
      }

      // Fetch attribute set
      const attributeSet = await getAttributeSetById(attributeSetId);
      if (!attributeSet) {
        console.error("Failed to load attribute set");
        setIsLoading(false);
        return;
      }

      // Initialize variables
      let variantPrices: VariantPrice[] = [];
      let variantImages: VariantImage[][] = [];
      let initialAttributeValues: Record<
        string,
        (string | number | boolean)[]
      > = {};

      if (product.isSimpleProduct) {
        // SIMPLE PRODUCT LOGIC (from first function)
        // Get the variant (simple products have exactly one variant)
        const variant = product.variants?.[0];

        if (variant) {
          // Set price from variant's basePrice (not the currentBasePrice)
          initialAttributeValues["price"] = [variant.basePrice || 0];
          initialAttributeValues["quantity"] = [variant.quantity || 0];

          // Process all attributes from the variant
          variant.attributes?.forEach((attr: any) => {
            // For chargeable variable options with multiple values
            if (attr.isVariable && attr.type === "options" && attr.isCharge) {
              if (!initialAttributeValues[attr.label]) {
                initialAttributeValues[attr.label] = [];
              }
              initialAttributeValues[attr.label].push(attr.value);

              // Store QTY and price for each option
              const index = initialAttributeValues[attr.label].length - 1;
              initialAttributeValues[`${attr.label}_qty_${index}`] = [
                attr.QTY || 0,
              ];
              initialAttributeValues[`${attr.label}_price_${index}`] = [
                attr.price || 0,
              ];
            }
            // For regular attributes
            else {
              initialAttributeValues[attr.label] = [attr.value];

              // Store additional fields for chargeable attributes
              if (attr.isCharge) {
                if (attr.QTY !== undefined) {
                  initialAttributeValues[`${attr.label}_qty`] = [attr.QTY];
                }
                if (attr.price !== undefined) {
                  initialAttributeValues[`${attr.label}_price`] = [attr.price];
                }
                if (attr.valueType) {
                  initialAttributeValues[`${attr.label}_valueType`] = [
                    attr.valueType,
                  ];
                }
              }
            }
          });
        }
      } else {
        // CUSTOMIZABLE PRODUCT LOGIC (from second function)
        if (response.allVariants) {
          // Get all option attributes that are used for variants (type: "options" and isVariable: false)
          const optionAttributes = attributeSet.attributes.filter(
            (attr) => attr.type === "options" && !attr.isVariable
          );
          const variantVideos = response.allVariants.map(
            (variant) => variant.video || ""
          );
          setExistingVariantVideos(variantVideos);

          // Initialize attribute values map for variant-defining attributes
          optionAttributes.forEach((attr) => {
            initialAttributeValues[attr._id] = [];
          });

          // Process each variant to extract attribute values
          response.allVariants.forEach((variant) => {
            variant.attributes.forEach((attr) => {
              const matchingAttr = optionAttributes.find(
                (a) => a.label === attr.label
              );
              if (
                matchingAttr &&
                !initialAttributeValues[matchingAttr._id].includes(attr.value)
              ) {
                initialAttributeValues[matchingAttr._id].push(attr.value);
              }
            });
          });

          // Now create variant combinations based on these values
          variantPrices = generateCombinations(
            optionAttributes,
            initialAttributeValues
          );

          // Map existing variant data to the generated combinations
          variantPrices = variantPrices.map((newVariant) => {
            const existingVariant = response.allVariants?.find((variant) => {
              return newVariant.combo.every((newAttr) => {
                return variant.attributes.some(
                  (existingAttr) =>
                    existingAttr.label === newAttr.label &&
                    existingAttr.value === newAttr.value
                );
              });
            });

            if (existingVariant) {
              // Create a mapping of all variable attributes (non-variant-defining)
              const variableValues: Record<string, VariantVariableValue> = {};
              const variableOptions: Record<string, VariableOptionValue[]> = {};

              // Process all attributes from the existing variant
              existingVariant.attributes.forEach((attr) => {
                const matchingAttr = attributeSet.attributes.find(
                  (a) => a.label === attr.label
                );

                if (!matchingAttr) return;

                // If it's a variable option attribute (like "Beats")
                if (
                  matchingAttr.isVariable &&
                  matchingAttr.type === "options"
                ) {
                  if (!variableOptions[matchingAttr._id]) {
                    variableOptions[matchingAttr._id] = [];
                  }

                  // Type assertion to access optional properties
                  const attrWithExtras = attr as VariantAttribute;

                  variableOptions[matchingAttr._id].push({
                    selectedOption: attr.value.toString(),
                    inputValue: attrWithExtras.QTY || 0,
                    type: "number",
                    price: attrWithExtras.price || 0,
                    QTY: attrWithExtras.QTY || 0,
                  });
                }
                // If it's a regular variable attribute (not variant-defining)
                else if (matchingAttr.isVariable) {
                  const attrWithExtras = attr as VariantAttribute;
                  variableValues[matchingAttr._id] = {
                    value: attr.value,
                    valueType: attrWithExtras.valueType || "fixed",
                    show: attrWithExtras.show !== false,
                  };
                }
              });

              return {
                ...newVariant,
                price: existingVariant.price,
                quantity: existingVariant.quantity,
                sku: existingVariant.sku,
                variableValues,
                variableOptions,
              };
            }
            return newVariant;
          });

          // Initialize variant images
          variantImages = response.allVariants.map((variant) =>
            variant.images.map((image) => ({
              path: image,
              id: `existing-${image}`, // Stable ID for existing images
              isExisting: true,
            }))
          );
        }
      }

      // Update state
      ReactDOM.unstable_batchedUpdates(() => {
        setSelectedSetId(attributeSetId);
        setSelectedSetAttributes(attributeSet.attributes);
        setAttributeValues(initialAttributeValues);
        setVariantPrices(variantPrices);
        setVariantImages(variantImages);
      });
      // toast.success("Product data loaded successfully");
    } catch (error) {
      console.error("Failed to fetch product", error);
      setError("Failed to load product data");
      toast.error("Failed to load product data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDynamicProductVideo = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      toast.warn("No video file was selected");
      return;
    }

    if (acceptedFiles.length > 1) {
      toast.error("Only one video allowed per product");
      return;
    }

    const videoFile = acceptedFiles[0];

    // Check if file is a video
    if (!videoFile.type.startsWith("video/")) {
      toast.error("Please select a valid video file");
      return;
    }

    // Check file size (e.g., 50MB limit)
    if (videoFile.size > 50 * 1024 * 1024) {
      toast.error("Video file size must be less than 50MB");
      return;
    }

    setDynamicProductVideo(videoFile);
    toast.success("Product video added successfully");
  };

  const handleRemoveDynamicProductVideo = () => {
    setDynamicProductVideo(null);
    if (existingDynamicProductVideo) {
      setExistingDynamicProductVideo(null);
    }
    toast.info("Video removed");
  };

  // Works for Simple Product
  // const fetchProductData = async (productId: string) => {
  //   try {
  //     setIsLoading(true);
  //     const response = await getProductById(productId);
  //     const product = response.product;

  //     // Handle main product images
  //     const productImages = Array.isArray(product.images)
  //       ? product.images
  //       : product.images
  //       ? [product.images]
  //       : [];
  //     setExistingProductImages(productImages);

  //     // Set basic info
  //     setProductName(product.name || "");
  //     setSku(product.sku || "");
  //     setActiveTab(product.isSimpleProduct ? "simple" : "customizable");
  //     setIsTaxable(product.isTaxable ?? false);
  //     setIsNewArrival(product.isNewArrival ?? false);
  //     setIsFeatured(product.isFeatured ?? false);
  //     setCurrentBasePrice(product.currentBasePrice || 0);

  //     // Handle category
  //     if (product.category) {
  //       setSelectedCategoryId(
  //         typeof product.category === "object"
  //           ? product.category._id
  //           : product.category
  //       );
  //     }

  //     // Handle attribute set
  //     const attributeSetId =
  //       typeof product.attributeSet === "object"
  //         ? product.attributeSet._id
  //         : product.attributeSet;
  //     setSelectedSetId(attributeSetId || "");

  //     // Initialize attribute values
  //     const initialAttributeValues: Record<
  //       string,
  //       (string | number | boolean)[]
  //     > = {};

  //     // Get the variant (simple products have exactly one variant)
  //     const variant = product.variants?.[0];

  //     if (variant) {
  //       // Set price from variant's basePrice (not the currentBasePrice)
  //       initialAttributeValues["price"] = [variant.basePrice || 0];
  //       initialAttributeValues["quantity"] = [variant.quantity || 0];

  //       // Process all attributes from the variant
  //       variant.attributes?.forEach((attr: any) => {
  //         // For chargeable variable options with multiple values
  //         if (attr.isVariable && attr.type === "options" && attr.isCharge) {
  //           if (!initialAttributeValues[attr.label]) {
  //             initialAttributeValues[attr.label] = [];
  //           }
  //           initialAttributeValues[attr.label].push(attr.value);

  //           // Store QTY and price for each option
  //           const index = initialAttributeValues[attr.label].length - 1;
  //           initialAttributeValues[`${attr.label}_qty_${index}`] = [
  //             attr.QTY || 0,
  //           ];
  //           initialAttributeValues[`${attr.label}_price_${index}`] = [
  //             attr.price || 0,
  //           ];
  //         }
  //         // For regular attributes
  //         else {
  //           initialAttributeValues[attr.label] = [attr.value];

  //           // Store additional fields for chargeable attributes
  //           if (attr.isCharge) {
  //             if (attr.QTY !== undefined) {
  //               initialAttributeValues[`${attr.label}_qty`] = [attr.QTY];
  //             }
  //             if (attr.price !== undefined) {
  //               initialAttributeValues[`${attr.label}_price`] = [attr.price];
  //             }
  //             if (attr.valueType) {
  //               initialAttributeValues[`${attr.label}_valueType`] = [
  //                 attr.valueType,
  //               ];
  //             }
  //           }
  //         }
  //       });
  //     }

  //     // Fetch attribute set details if we have an ID
  //     if (attributeSetId) {
  //       const attributeSet = await getAttributeSetById(attributeSetId);
  //       if (attributeSet) {
  //         setSelectedSetAttributes(attributeSet.attributes || []);
  //       }
  //     }

  //     // Update state
  //     ReactDOM.unstable_batchedUpdates(() => {
  //       setAttributeValues(initialAttributeValues);
  //     });
  //   } catch (error) {
  //     console.error("Failed to fetch product", error);
  //     setError("Failed to load product data");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // Works for customizable Product
  // const fetchProductData = async (productId: string) => {
  //   try {
  //     setIsLoading(true);
  //     const response = await getProductById(productId);
  //     console.log("Proper response", response);
  //     const product = response.product;

  //     // Handle main product images
  //     const productImages = Array.isArray(response.product.images)
  //       ? response.product.images
  //       : response.product.images
  //       ? [response.product.images]
  //       : [];

  //     setExistingProductImages(productImages);

  //     // Set basic info
  //     setProductName(product.name || "");
  //     setSku(product.sku || "");
  //     setActiveTab(product.isSimpleProduct ? "simple" : "customizable");
  //     setIsTaxable(product.isTaxable ?? product.taxable ?? false);
  //     setIsNewArrival(product.isNewArrival ?? false);
  //     setIsFeatured(product.isFeatured ?? false);

  //     // Handle category
  //     if (product.category) {
  //       setSelectedCategoryId(
  //         typeof product.category === "object"
  //           ? product.category._id
  //           : product.category
  //       );
  //     }

  //     // Handle attribute set
  //     const attributeSetId =
  //       typeof product.attributeSet === "object"
  //         ? product.attributeSet._id
  //         : product.attributeSet;

  //     if (!attributeSetId) {
  //       console.warn("No attribute set found for product");
  //       setIsLoading(false);
  //       return;
  //     }

  //     // Fetch attribute set
  //     const attributeSet = await getAttributeSetById(attributeSetId);
  //     if (!attributeSet) {
  //       console.error("Failed to load attribute set");
  //       setIsLoading(false);
  //       return;
  //     }

  //     // Process variants if it's customizable
  //     let variantPrices: VariantPrice[] = [];
  //     let variantImages: VariantImage[][] = [];
  //     let initialAttributeValues: Record<
  //       string,
  //       (string | number | boolean)[]
  //     > = {};

  //     if (!product.isSimpleProduct && response.allVariants) {
  //       // Get all option attributes that are used for variants (type: "options" and isVariable: false)
  //       const optionAttributes = attributeSet.attributes.filter(
  //         (attr) => attr.type === "options" && !attr.isVariable
  //       );

  //       // Initialize attribute values map for variant-defining attributes
  //       optionAttributes.forEach((attr) => {
  //         initialAttributeValues[attr._id] = [];
  //       });

  //       // Process each variant to extract attribute values
  //       response.allVariants.forEach((variant) => {
  //         variant.attributes.forEach((attr) => {
  //           const matchingAttr = optionAttributes.find(
  //             (a) => a.label === attr.label
  //           );
  //           if (
  //             matchingAttr &&
  //             !initialAttributeValues[matchingAttr._id].includes(attr.value)
  //           ) {
  //             initialAttributeValues[matchingAttr._id].push(attr.value);
  //           }
  //         });
  //       });

  //       // Now create variant combinations based on these values
  //       variantPrices = generateCombinations(
  //         optionAttributes,
  //         initialAttributeValues
  //       );

  //       // Map existing variant data to the generated combinations
  //       variantPrices = variantPrices.map((newVariant) => {
  //         const existingVariant = response.allVariants?.find((variant) => {
  //           return newVariant.combo.every((newAttr) => {
  //             return variant.attributes.some(
  //               (existingAttr) =>
  //                 existingAttr.label === newAttr.label &&
  //                 existingAttr.value === newAttr.value
  //             );
  //           });
  //         });

  //         if (existingVariant) {
  //           // Create a mapping of all variable attributes (non-variant-defining)
  //           const variableValues: Record<string, VariantVariableValue> = {};
  //           const variableOptions: Record<string, VariableOptionValue[]> = {};

  //           // Process all attributes from the existing variant
  //           existingVariant.attributes.forEach((attr) => {
  //             const matchingAttr = attributeSet.attributes.find(
  //               (a) => a.label === attr.label
  //             );

  //             if (!matchingAttr) return;

  //             // If it's a variable option attribute (like "Beats")
  //             if (matchingAttr.isVariable && matchingAttr.type === "options") {
  //               if (!variableOptions[matchingAttr._id]) {
  //                 variableOptions[matchingAttr._id] = [];
  //               }

  //               // Type assertion to access optional properties
  //               const attrWithExtras = attr as VariantAttribute;

  //               variableOptions[matchingAttr._id].push({
  //                 selectedOption: attr.value.toString(),
  //                 inputValue: attrWithExtras.QTY || 0,
  //                 type: "number",
  //                 price: attrWithExtras.price || 0,
  //                 QTY: attrWithExtras.QTY || 0,
  //               });
  //             }
  //             // If it's a regular variable attribute (not variant-defining)
  //             else if (matchingAttr.isVariable) {
  //               const attrWithExtras = attr as VariantAttribute;
  //               variableValues[matchingAttr._id] = {
  //                 value: attr.value,
  //                 valueType: attrWithExtras.valueType || "fixed",
  //                 show: attrWithExtras.show !== false,
  //               };
  //             }
  //           });

  //           return {
  //             ...newVariant,
  //             price: existingVariant.price,
  //             quantity: existingVariant.quantity,
  //             sku: existingVariant.sku,
  //             variableValues,
  //             variableOptions,
  //           };
  //         }
  //         return newVariant;
  //       });

  //       // Initialize variant images
  //       variantImages = response.allVariants.map((variant) =>
  //         variant.images.map((image) => ({
  //           path: image,
  //           id: `existing-${image}`, // Stable ID for existing images
  //           isExisting: true,
  //         }))
  //       );
  //     }

  //     // Update state
  //     ReactDOM.unstable_batchedUpdates(() => {
  //       setSelectedSetId(attributeSetId);
  //       setSelectedSetAttributes(attributeSet.attributes);
  //       setAttributeValues(initialAttributeValues);
  //       setVariantPrices(variantPrices);
  //       setVariantImages(variantImages);
  //       setExistingProductImages(productImages); // This sets the main product images
  //     });
  //   } catch (error) {
  //     console.error("Failed to fetch product", error);
  //     setError("Failed to load product data");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

useEffect(() => {
  return () => {
    // Clean up image object URLs
    variantImages.forEach((imageGroup) => {
      imageGroup.forEach((img) => {
        if (img.path.startsWith("blob:") && !img.isExisting) {
          URL.revokeObjectURL(img.path);
        }
      });
    });

    // Clean up video object URLs
    if (productVideo) {
      URL.revokeObjectURL(URL.createObjectURL(productVideo));
    }

    variantVideos.forEach((video) => {
      if (video) {
        URL.revokeObjectURL(URL.createObjectURL(video));
      }
    });
  };
}, [variantImages, productVideo, variantVideos]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch attribute sets
        const fetchAttributeSets = async () => {
          try {
            const data = await getAllAttributeSets();
            console.log("Attribute Sets:", data);
            setAttributeSets(data);
            // toast.success("Attribute sets loaded successfully");
          } catch (error) {
            console.error("Failed to fetch attribute sets:", error);
            toast.error("Failed to load attribute sets");
          }
        };
        fetchAttributeSets();

        // Fetch base price
        const basePriceData = await fetchBasePrice();
        if (basePriceData?.price) {
          setBasePrice24K(basePriceData.price.toString());
          setCurrentBasePrice(basePriceData.price);
          // toast.success("Base price loaded successfully");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        // setError("Failed to load initial data. Please try again later.");
        toast.error("Failed to load initial data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleProductVideo = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      toast.warn("No video file was selected");
      return;
    }

    if (acceptedFiles.length > 1) {
      toast.error("Only one video allowed per product");
      return;
    }

    const videoFile = acceptedFiles[0];

    // Check if file is a video
    if (!videoFile.type.startsWith("video/")) {
      toast.error("Please select a valid video file");
      return;
    }

    // Check file size (e.g., 50MB limit)
    if (videoFile.size > 50 * 1024 * 1024) {
      toast.error("Video file size must be less than 50MB");
      return;
    }

    setProductVideo(videoFile);
    toast.success("Product video added successfully");
  };

  const handleRemoveProductVideo = () => {
    setProductVideo(null);
    if (existingProductVideo) {
      setExistingProductVideo(null);
    }
    toast.info("Video removed");
  };

  const handleVariantVideo = (variantIndex: number, acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      toast.warn("No video file was selected");
      return;
    }

    if (acceptedFiles.length > 1) {
      toast.error("Only one video allowed per variant");
      return;
    }

    const videoFile = acceptedFiles[0];

    // Check if file is a video
    if (!videoFile.type.startsWith("video/")) {
      toast.error("Please select a valid video file");
      return;
    }

    // Check file size
    if (videoFile.size > 50 * 1024 * 1024) {
      toast.error("Video file size must be less than 50MB");
      return;
    }

    setVariantVideos((prev) => {
      const updated = [...prev];
      updated[variantIndex] = videoFile;
      return updated;
    });

    toast.success("Variant video added successfully");
  };

  const handleRemoveVariantVideo = (variantIndex: number) => {
    setVariantVideos((prev) => {
      const updated = [...prev];
      updated[variantIndex] = null;
      return updated;
    });

    if (existingVariantVideos[variantIndex]) {
      setExistingVariantVideos((prev) => {
        const updated = [...prev];
        updated[variantIndex] = "";
        return updated;
      });
    }

    toast.info("Variant video removed");
  };

  const handleExportProducts = async () => {
    try {
      const response = await exportProductsToCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "products_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Products exported successfully");
    } catch (error) {
      toast.error("Export failed");
      console.error("Export error:", error);
    }
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFile(e.target.files[0]);
    }
  };

  const handleImportProducts = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const response = await importProductsFromCSV(
        importFile,
        (progressEvent: AxiosProgressEvent) => {
          // Add null check for progressEvent.total
          const percentCompleted = progressEvent.total
            ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
            : 0;
          setImportProgress(percentCompleted);
        }
      );

      setImportResult(response);

      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message || "Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Import failed");
    } finally {
      setIsImporting(false);
      setImportFile(null);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await downloadTemplateCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "product_import_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded successfully");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  function generateCombinations(
    optionAttrs: Attribute[],
    valuesMap: Record<string, (string | number | boolean)[]>
  ) {
    const validOptionAttrs =
      optionAttrs?.filter(
        (attr) => valuesMap[attr._id] && valuesMap[attr._id].length > 0
      ) || [];

    if (validOptionAttrs.length === 0) return [];

    const selectedValues = validOptionAttrs.map((attr) =>
      (valuesMap[attr._id] || []).map((value) => ({
        attrId: attr._id,
        label: attr.label,
        value,
        type: attr.type, // Add the type property
        isVariable: attr.isVariable, // Add isVariable if needed
      }))
    );

    const cartesian = (...arrays: VariantAttribute[][]) => {
      return arrays
        .reduce((acc, curr) => acc.flatMap((x) => curr.map((y) => [...x, y])), [
          [],
        ] as VariantAttribute[][])
        .filter((combo) => combo.length > 0);
    };

    const combinations = cartesian(...selectedValues);
    return combinations.map((combo) => ({
      combo,
      price: calculateVariantPrice(combo),
      quantity: 0,
      variableValues: {} as Record<string, VariantVariableValue>,
      variableOptions: {} as Record<string, VariableOptionValue[]>, // Initialize empty object
    }));
  }

  const handleAddVariableOption = (variantIndex: number, attrId: string) => {
    setSelectedVariableOptions((prev) => {
      const newState = { ...prev };
      if (!newState[variantIndex]) newState[variantIndex] = {};
      if (!newState[variantIndex][attrId]) newState[variantIndex][attrId] = [];
      return newState;
    });
  };

  const handleRemoveVariableOption = (
    variantIndex: number,
    attrId: string,
    valueIndex: number
  ) => {
    setVariantPrices((prev) => {
      const updated = [...prev];
      if (updated[variantIndex].variableOptions?.[attrId]) {
        // Remove the specific option
        updated[variantIndex].variableOptions![attrId] = updated[
          variantIndex
        ].variableOptions![attrId].filter((_, idx) => idx !== valueIndex);

        // If no options left for this attribute, remove the attribute entry
        if (updated[variantIndex].variableOptions![attrId].length === 0) {
          delete updated[variantIndex].variableOptions![attrId];
        }
      }
      return updated;
    });
  };

  const handleVariableOptionChange = (
    variantIndex: number,
    attrId: string,
    valueIndex: number,
    selectedOption: string // Now only handles the dropdown selection
  ) => {
    setVariantPrices((prev) => {
      const updated = [...prev];
      if (!updated[variantIndex].variableOptions) {
        updated[variantIndex].variableOptions = {};
      }
      if (!updated[variantIndex].variableOptions![attrId]) {
        updated[variantIndex].variableOptions![attrId] = [];
      }

      const existingOption = updated[variantIndex].variableOptions![attrId][
        valueIndex
      ] || {
        selectedOption: "",
        inputValue: "",
        type: "text",
        price: 0,
      };

      updated[variantIndex].variableOptions![attrId][valueIndex] = {
        ...existingOption,
        selectedOption: selectedOption,
      };

      return updated;
    });
  };

  const handleVariableInputChange = (
    variantIndex: number,
    attrId: string,
    valueIndex: number,
    inputValue: string | number
  ) => {
    setVariantPrices((prev) => {
      const updated = [...prev];
      updated[variantIndex].variableOptions![attrId][valueIndex].inputValue =
        inputValue;
      return updated;
    });
  };

  const handleVariableOptionTypeChange = (
    variantIndex: number,
    attrId: string,
    valueIndex: number,
    type: "text" | "number"
  ) => {
    setVariantPrices((prev) => {
      const updated = [...prev];
      if (!updated[variantIndex].variableOptions) {
        updated[variantIndex].variableOptions = {};
      }
      if (!updated[variantIndex].variableOptions![attrId]) {
        updated[variantIndex].variableOptions![attrId] = [];
      }
      if (!updated[variantIndex].variableOptions![attrId][valueIndex]) {
        updated[variantIndex].variableOptions![attrId][valueIndex] = {
          selectedOption: "",
          inputValue: type === "number" ? 0 : "",
          type,
          price: 0,
        };
      } else {
        updated[variantIndex].variableOptions![attrId][valueIndex] = {
          ...updated[variantIndex].variableOptions![attrId][valueIndex],
          type,
          inputValue: type === "number" ? 0 : "",
        };
      }
      return updated;
    });
  };

  const handleVariableOptionPriceChange = (
    variantIndex: number,
    attrId: string,
    valueIndex: number,
    price: number
  ) => {
    setVariantPrices((prev) => {
      const updated = [...prev];
      if (!updated[variantIndex].variableOptions) {
        updated[variantIndex].variableOptions = {};
      }
      if (!updated[variantIndex].variableOptions![attrId]) {
        updated[variantIndex].variableOptions![attrId] = [];
      }
      if (!updated[variantIndex].variableOptions![attrId][valueIndex]) {
        updated[variantIndex].variableOptions![attrId][valueIndex] = {
          selectedOption: "",
          inputValue: "",
          type: "text",
          price,
        };
      } else {
        updated[variantIndex].variableOptions![attrId][valueIndex] = {
          ...updated[variantIndex].variableOptions![attrId][valueIndex],
          price,
        };
      }
      return updated;
    });
  };

  const calculateVariantPrice = (combo: VariantAttribute[]) => {
    const karatObj = combo.find((c) => c.label.toLowerCase() === "karat");

    // If there's no karat attribute in the variant, return 0 or some default price
    if (!karatObj || !currentBasePrice) return 0;

    const karatValue =
      typeof karatObj.value === "string" ? karatObj.value.replace("K", "") : "";
    const percentage = karatMap[karatValue] || 0;
    return parseFloat(((currentBasePrice * percentage) / 100).toFixed(2));
  };

  const handleAttributeChange = (
    attrId: string,
    value: string | number | boolean
  ) => {
    const attr = selectedSetAttributes.find((a) => a._id === attrId);
    let processedValue: string | number | boolean = value;

    if (attr?.type === "number") {
      processedValue = value === "" ? "" : Number(value);
    } else if (attr?.type === "boolean") {
      processedValue =
        typeof value === "string" ? value === "true" : Boolean(value);
    }

    setAttributeValues((prev) => ({
      ...prev,
      [attrId]: [processedValue],
    }));
  };

  const handleMultiSelect = (attrId: string, value: string) => {
    const existing = attributeValues[attrId] || [];
    if (existing.includes(value)) {
      setAttributeValues((prev) => ({
        ...prev,
        [attrId]: existing.filter((v) => v !== value),
      }));
    } else {
      setAttributeValues((prev) => ({
        ...prev,
        [attrId]: [...existing, value],
      }));
    }
  };

  const resetForm = () => {
    setProductName("");
    setSku("");
    setSelectedSetId("");
    setSelectedSetAttributes([]);
    setAttributeValues({});
    setVariantPrices([]);
    setVariantImages([]);
    setProductImages([]);
    setSelectedCategoryIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setError(null);

    const toastId = toast.loading(
      isEditMode ? "Updating product..." : "Creating product..."
    );

    try {
      const formData = new FormData();
      const cleanExistingImages = existingProductImages.filter(
        (img) => typeof img === "string" && img.trim() !== ""
      );

      if (activeTab === "simple") {
        const hasExplicitPrice =
          attributeValues["price"]?.[0] !== undefined &&
          attributeValues["price"]?.[0] !== "";
        const explicitPrice = hasExplicitPrice
          ? Number(attributeValues["price"]?.[0])
          : 0;

        const simpleAttributes = selectedSetAttributes
          .filter((attr) => attr.label !== "Price")
          .flatMap((attr) => {
            if (attr.type === "options" && attr.isVariable && attr.isCharge) {
              return (attributeValues[attr.label] || [])
                .map((value, index) => ({
                  label: attr.label,
                  value,
                  type: attr.type,
                  isVariable: attr.isVariable,
                  isCharge: attr.isCharge,
                  QTY:
                    Number(
                      attributeValues[`${attr.label}_qty_${index}`]?.[0]
                    ) || 0,
                  price:
                    Number(
                      attributeValues[`${attr.label}_price_${index}`]?.[0]
                    ) || 0,
                }))
                .filter((item) => item.value);
            }

            if (attr.isCharge && attr.isVariable && attr.type === "number") {
              return [
                {
                  label: attr.label,
                  value:
                    attr.type === "number"
                      ? Number(attributeValues[attr.label]?.[0] || 0)
                      : attr.type === "boolean"
                      ? attributeValues[attr.label]?.[0] === "true"
                      : attributeValues[attr.label]?.[0] || "",
                  type: attr.type,
                  isVariable: attr.isVariable || false,
                  isCharge: attr.isCharge || false,
                  QTY: Number(attributeValues[`${attr.label}_qty`]?.[0]) || 0,
                  price:
                    Number(attributeValues[`${attr.label}_price`]?.[0]) || 0,
                  valueType:
                    attributeValues[`${attr.label}_valueType`]?.[0] || "fixed",
                },
              ];
            }

            return [
              {
                label: attr.label,
                value:
                  attr.type === "number"
                    ? Number(attributeValues[attr.label]?.[0] || 0)
                    : attr.type === "boolean"
                    ? attributeValues[attr.label]?.[0] === "true"
                    : attributeValues[attr.label]?.[0] || "",
                type: attr.type,
                isVariable: attr.isVariable || false,
                isCharge: attr.isCharge || false,
                QTY: 0,
                price: 0,
              },
            ];
          });

        const productData = {
          name: productName,
          sku,
          attributeSetId: selectedSetId || null,
          isSimpleProduct: true,
          price: explicitPrice,
          quantity: Number(attributeValues["quantity"]?.[0]) || 0,
          attributes: simpleAttributes,
          currentBasePrice: explicitPrice || currentBasePrice || 0,
          productId: id,
          isTaxable,
          isNewArrival,
          isFeatured,
          categoryId: selectedCategoryIds,
          usesExplicitPrice: hasExplicitPrice,
          skipAdditionalCharges: !!explicitPrice,
        };

        formData.append("productData", JSON.stringify(productData));
        productImages.forEach((file) => formData.append("productImages", file));

        // FIX: Append each existing image individually to avoid nested array structure
        cleanExistingImages.forEach((img) => {
          formData.append("existingProductImages", img);
        });
      } else {
        const variants = variantPrices.map((variant, i) => {
          const combo = Array.isArray(variant.combo) ? variant.combo : [];

          const variableOptionAttributes = Object.entries(
            variant.variableOptions || {}
          ).flatMap(([attrId, options]) => {
            const attr = selectedSetAttributes.find((a) => a._id === attrId);
            return attr
              ? options.map((option) => ({
                  attrId,
                  label: attr.label,
                  value: option.selectedOption,
                  type: attr.type,
                  isVariable: true,
                  isCharge: attr.isCharge,
                  QTY: Number(option.inputValue) || 0,
                  price: option.price,
                  valueType: attr.type === "number" ? "fixed" : undefined,
                }))
              : [];
          });

          return {
            sku: variant.sku || `${sku}-${i + 1}`,
            price: variant.price || 0,
            quantity: variant.quantity || 0,
            attributes: [
              // From combo (e.g., Karat, Size)
              ...combo.map(({ label, value }) => {
                const attr = selectedSetAttributes.find(
                  (a) => a.label === label
                );
                return {
                  attrId: attr?._id || "",
                  label,
                  value: attr?.type === "number" ? Number(value) : value,
                  type: attr?.type || "options",
                  isVariable: attr?.isVariable || false,
                  isCharge: attr?.isCharge || false,
                  show: true,
                };
              }),

              // Fixed attributes (non-variable, non-options)
              ...selectedSetAttributes
                .filter((attr) => attr.type !== "options" && !attr.isVariable)
                .map((attr) => ({
                  attrId: attr._id,
                  label: attr.label,
                  value:
                    attr.type === "number"
                      ? Number(attributeValues[attr._id]?.[0] || 0)
                      : attributeValues[attr._id]?.[0] || "",
                  type: attr.type,
                  isVariable: attr.isVariable,
                  isCharge: attr.isCharge,
                  show: true,
                })),

              // Variable number-type attributes
              ...selectedSetAttributes
                .filter((attr) => attr.type !== "options" && attr.isVariable)
                .map((attr) => {
                  const baseValue = variant.variableValues?.[attr._id]?.value;
                  return {
                    attrId: attr._id,
                    label: attr.label,
                    value:
                      attr.type === "number"
                        ? Number(baseValue || 0)
                        : baseValue || "",
                    type: attr.type,
                    isVariable: attr.isVariable,
                    isCharge: attr.isCharge,
                    show: variant.variableValues?.[attr._id]?.show ?? true,
                    ...(attr.isCharge && attr.type === "number"
                      ? {
                          valueType:
                            variant.variableValues?.[attr._id]?.valueType ||
                            "fixed",
                        }
                      : {}),
                  };
                }),

              // Variable Options (from dropdowns with quantity/price)
              ...Object.entries(variant.variableOptions || {}).flatMap(
                ([attrId, options]) => {
                  const attr = selectedSetAttributes.find(
                    (a) => a._id === attrId
                  );
                  return attr
                    ? options.map((option) => ({
                        attrId,
                        label: attr.label,
                        value: option.selectedOption,
                        type: attr.type,
                        isVariable: attr.isVariable,
                        isCharge: attr.isCharge,
                        QTY: Number(option.inputValue) || 0,
                        price: option.price,
                        ...(attr.type === "number"
                          ? { valueType: "fixed" }
                          : {}),
                      }))
                    : [];
                }
              ),
            ],
          };
        });

        formData.append(
          "productData",
          JSON.stringify({
            name: productName,
            sku,
            attributeSetId: selectedSetId,
            variants,
            currentBasePrice,
            isSimpleProduct: false,
            productId: id,
            isTaxable,
            isNewArrival,
            isFeatured,
            categoryId: selectedCategoryIds,
          })
        );

        productImages.forEach((file) => formData.append("productImages", file));

        // FIX: Append each existing image individually to avoid nested array structure
        cleanExistingImages.forEach((img) => {
          formData.append("existingProductImages", img);
        });

        variantImages.forEach((images, variantIndex) => {
          images.forEach((img) => {
            if (img.file)
              formData.append(`variantImages[${variantIndex}]`, img.file);
          });

          const existingImgs = images
            .filter(
              (img) => img.isExisting && !img.markedForDeletion && img.path
            )
            .map((img) => img.path);

          if (existingImgs.length > 0) {
            formData.append(
              `existingVariantImages[${variantIndex}]`,
              JSON.stringify(existingImgs)
            );
          }
        });
      }
      // For simple product
      if (productVideo) {
        formData.append("productVideo", productVideo);
      }

      // For existing product video (in edit mode)
      if (existingProductVideo && !productVideo) {
        formData.append("existingProductVideo", existingProductVideo);
      }

      // For customizable product variants
      variantVideos.forEach((video, variantIndex) => {
        if (video) {
          formData.append(`variantVideos[${variantIndex}]`, video);
        }
      });

      // For existing variant videos
      existingVariantVideos.forEach((videoUrl, variantIndex) => {
        if (videoUrl && !variantVideos[variantIndex]) {
          formData.append(`existingVariantVideos[${variantIndex}]`, videoUrl);
        }
      });

      if (isEditMode && id) {
        formData.append("productId", id);
        const { success, product, error } = await updateProductById(formData);

        if (success) {
          setSubmitSuccess(true);
          setSubmitSuccessMessage("Product updated successfully!");
          toast.update(toastId, {
            render: "Product updated successfully!",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
          setTimeout(() => navigate("/products-list"), 1500);
        } else {
          throw new Error(error || "Product update failed");
        }
      } else {
        const response = await addProduct(formData);
        if (response.success) {
          setSubmitSuccess(true);
          resetForm();
          toast.update(toastId, {
            render: "Product created successfully!",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
          setTimeout(() => navigate("/products-list"), 1500);
        } else {
          throw new Error(response.message || "Failed to create product");
        }
      }
    } catch (err: any) {
      // setError(err.message);
      toast.update(toastId, {
        render: err.message || "An error occurred while saving the product",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this helper function

  const handleProductImages = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      toast.warn("No files were selected");
      return;
    }
    const totalImages =
      existingProductImages.length +
      productImages.length +
      acceptedFiles.length;
    if (totalImages > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    setProductImages([...productImages, ...acceptedFiles]);
    toast.success(`${acceptedFiles.length} image(s) added successfully`);
  };

  const getFormControlValue = (
    value: string | number | boolean | undefined
  ): string | number => {
    if (value === undefined) return "";
    if (typeof value === "boolean") return value ? "true" : "false";
    return value;
  };

  useEffect(() => {
    if (!Array.isArray(selectedSetAttributes)) return;

    const optionAttrs =
      selectedSetAttributes?.filter((attr) => attr.type === "options") || [];

    const optionValuesMap = optionAttrs.reduce((acc, attr) => {
      acc[attr._id] = (attributeValues[attr._id] || []).map((val) =>
        val.toString()
      );
      return acc;
    }, {} as Record<string, string[]>);

    const combos = generateCombinations(optionAttrs, optionValuesMap);

    setVariantPrices((prev) => {
      return combos.map((newCombo, index) => {
        const comboKey = newCombo.combo
          .map((c: VariantAttribute) => `${c.attrId}:${c.value}`)
          .join("|");

        // Find existing variant that matches this combination
        const existing = prev.find(
          (v) =>
            v.combo &&
            v.combo
              .map((c: VariantAttribute) => `${c.attrId}:${c.value}`)
              .join("|") === comboKey
        );

        const price = calculateVariantPrice(newCombo.combo);

        // Preserve existing images if they exist
        const existingImages = existing
          ? variantImages[prev.indexOf(existing)]
          : variantImages[index] || [];

        return existing
          ? {
              ...existing,
              price,
              // Preserve variable values and options
              variableValues: existing.variableValues || {},
              variableOptions: existing.variableOptions || {},
            }
          : {
              ...newCombo,
              price,
              // Initialize with empty objects if not present
              variableValues: {},
              variableOptions: {},
            };
      });
    });

    // Only initialize empty image arrays for new variants
    setVariantImages((prev) => {
      const newImages = combos.map((_, index) => prev[index] || []);
      return newImages;
    });
  }, [attributeValues, selectedSetAttributes, currentBasePrice, karatMap]);

  const handleSetChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const setId = e.target.value;
    setSelectedSetId(setId);

    if (!setId) {
      setSelectedSetAttributes([]);
      setAttributeValues({});
      setVariantPrices([]);
      return;
    }

    try {
      const selectedSet = await getAttributeSetById(setId);
      setSelectedSetAttributes(selectedSet.attributes || []);
      setAttributeValues({});
      setVariantPrices([]);
      // toast.success("Attribute set loaded successfully");
    } catch (error) {
      console.error("Failed to fetch attribute set:", error);
      setSelectedSetAttributes([]);
      toast.error("Failed to load attribute set");
    }
  };

  const handleRemoveProductImage = (index: number) => {
    setProductImages(productImages.filter((_, i) => i !== index));
    toast.info("Image removed");
  };

  const handleRemoveExistingProductImage = (index: number) => {
    // Create a copy and remove the image
    const updated = [...existingProductImages];
    updated.splice(index, 1);
    setExistingProductImages(updated);
    toast.info("Image removed");
  };

  const handleRemoveVariantImage = (
    variantIndex: number,
    imageIndex: number
  ) => {
    setVariantImages((prev) => {
      const updated = prev.map((images, vIdx) => {
        if (vIdx !== variantIndex) return images;

        const newImages = [...images];
        const image = newImages[imageIndex];

        if (image.isExisting) {
          newImages[imageIndex] = {
            ...image,
            markedForDeletion: true,
          };
        } else {
          newImages.splice(imageIndex, 1);
        }

        return newImages;
      });

      return updated;
    });

    toast.info("Variant image removed");
  };

  // const handleBasePriceSubmit = async () => {
  //   try {
  //     await axios.post("http://localhost:5000/api/base-price", {
  //       price: Number(basePrice24K),
  //     });
  //     setCurrentBasePrice(Number(basePrice24K));
  //     alert("Base 24K price saved successfully!");
  //   } catch (err) {
  //     console.error(err);
  //     alert("Failed to save base price");
  //   }
  // };

  const handleVariantImages = async (index: number, files: File[]) => {
    if (files.length === 0) {
      toast.warn("No files were selected");
      return;
    }

    if (variantImages[index]?.length + files.length > 5) {
      toast.error("Maximum 5 images per variant allowed");
      return;
    }

    const newImages = files.map((file) => ({
      file,
      path: URL.createObjectURL(file),
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      isExisting: false,
    }));

    setVariantImages((prev) => {
      const updated = [...prev];
      updated[index] = [
        ...(updated[index] || []), // Keep existing images
        ...newImages, // Add new images
      ];
      return updated;
    });
    toast.success(`${files.length} image(s) added to variant`);
  };
  useEffect(() => {
    return () => {
      // Clean up object URLs when component unmounts
      variantImages.forEach((imageGroup) => {
        imageGroup.forEach((img) => {
          if (img.path.startsWith("blob:") && !img.isExisting) {
            URL.revokeObjectURL(img.path);
          }
        });
      });
    };
  }, [variantImages]);

  const getAttributeValue = (attrId: string): string | number => {
    const value = attributeValues[attrId]?.[0] as
      | string
      | number
      | boolean
      | undefined;
    if (value === undefined) return "";
    return typeof value === "boolean" ? value.toString() : value;
  };

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading data...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="alert alert-danger">{error}</div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Create Product" pageTitle="Products" />
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-end gap-2">
                <Button
                  variant="outline-info"
                  onClick={downloadTemplate}
                  className="me-2"
                >
                  <i className="ri-file-download-line me-1"></i> Download
                  Template
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowImportModal(true)}
                >
                  <i className="ri-upload-line me-1"></i> Import Products
                </Button>
                <Button
                  variant="outline-success"
                  onClick={handleExportProducts}
                >
                  <i className="ri-download-line me-1"></i> Export Products
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <div className="d-flex border-bottom">
                <Button
                  variant="link"
                  className={`me-2 text-decoration-none ${
                    activeTab === "simple"
                      ? "border-bottom border-primary border-2 text-primary"
                      : "text-muted"
                  }`}
                  onClick={() => setActiveTab("simple")}
                >
                  Simple Product
                </Button>
                <Button
                  variant="link"
                  className={`text-decoration-none ${
                    activeTab === "customizable"
                      ? "border-bottom border-primary border-2 text-primary"
                      : "text-muted"
                  }`}
                  onClick={() => setActiveTab("customizable")}
                >
                  Customizable Product
                </Button>
              </div>
            </Col>
          </Row>
          {activeTab === "simple" ? (
            <form onSubmit={handleSubmit}>
              <Card>
                <Card.Header>
                  <div className="d-flex">
                    <div className="flex-shrink-0 me-3">
                      <div className="avatar-sm">
                        <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                          <i className="bi bi-box-seam"></i>
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="card-title mb-1">
                        Simple Product Information
                      </h5>
                      <p className="text-muted mb-0">
                        Basic product details. All fields are optional.
                      </p>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Form.Label htmlFor="simple-sku-input">
                          SKU (Optional)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="simple-sku-input"
                          value={sku}
                          onChange={(e) => setSku(e.target.value)}
                          placeholder="Enter product SKU"
                        />
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Form.Label htmlFor="simple-name-input">
                          Product Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="simple-name-input"
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          placeholder="Enter product name"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Form.Label>Product Images (max 5 optional)</Form.Label>
                        <Dropzone
                          onDrop={handleProductImages}
                          accept={{
                            "image/*": [".jpeg", ".jpg", ".png", ".gif"],
                          }}
                          maxFiles={
                            5 -
                            (existingProductImages.length +
                              productImages.length)
                          }
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone dz-clickable text-center">
                              <div
                                className="dz-message needsclick"
                                {...getRootProps()}
                              >
                                <div className="mb-3">
                                  <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                </div>
                                <h5>Drop files here or click to upload.</h5>
                                <p className="text-muted">
                                  {existingProductImages.length +
                                    productImages.length}{" "}
                                  of 5 images uploaded
                                </p>
                              </div>
                            </div>
                          )}
                        </Dropzone>

                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {/* Display existing images from API */}
                          {existingProductImages.map((imageUrl, idx) => (
                            <div
                              key={`existing-${idx}`}
                              className="image-thumbnail-wrapper position-relative"
                            >
                              <img
                                src={imageUrl}
                                alt={`Product ${idx + 1}`}
                                className="image-thumbnail"
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  border: "2px solid #e9ecef",
                                }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="image-remove-btn position-absolute"
                                style={{
                                  top: "-5px",
                                  right: "-5px",
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  padding: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  lineHeight: "1",
                                  zIndex: 10,
                                }}
                                onClick={() =>
                                  handleRemoveExistingProductImage(idx)
                                }
                              >
                                ×
                              </Button>
                            </div>
                          ))}

                          {/* New uploads */}
                          {productImages.map((file, idx) => (
                            <div
                              key={`new-${idx}`}
                              className="image-thumbnail-wrapper position-relative"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`New upload ${idx + 1}`}
                                className="image-thumbnail"
                                style={{
                                  width: "100px",
                                  height: "100px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  border: "2px solid #e9ecef",
                                }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="image-remove-btn position-absolute"
                                style={{
                                  top: "-5px",
                                  right: "-5px",
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  padding: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  lineHeight: "1",
                                  zIndex: 10,
                                }}
                                onClick={() => handleRemoveProductImage(idx)}
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Form.Label>Product Video (Optional)</Form.Label>
                        <Dropzone
                          onDrop={handleProductVideo}
                          accept={{
                            "video/*": [
                              ".mp4",
                              ".mov",
                              ".avi",
                              ".mkv",
                              ".webm",
                            ],
                          }}
                          maxFiles={1}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone dz-clickable text-center">
                              <div
                                className="dz-message needsclick"
                                {...getRootProps()}
                              >
                                <div className="mb-3">
                                  <i className="display-4 text-muted ri-video-line" />
                                </div>
                                <h5>
                                  Drop video file here or click to upload.
                                </h5>
                                <p className="text-muted">
                                  Max 50MB, one video allowed
                                </p>
                              </div>
                            </div>
                          )}
                        </Dropzone>

                        {/* Display current video */}
                        {(productVideo || existingProductVideo) && (
                          <div className="mt-3">
                            <div className="video-preview-wrapper position-relative">
                              <video
                                controls
                                src={
                                  productVideo
                                    ? URL.createObjectURL(productVideo)
                                    : existingProductVideo || ""
                                }
                                className="video-thumbnail"
                                style={{
                                  width: "100%",
                                  maxHeight: "200px",
                                  borderRadius: "8px",
                                  border: "2px solid #e9ecef",
                                }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="video-remove-btn position-absolute"
                                style={{
                                  top: "-5px",
                                  right: "-5px",
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  padding: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  lineHeight: "1",
                                  zIndex: 10,
                                }}
                                onClick={handleRemoveProductVideo}
                              >
                                ×
                              </Button>
                            </div>
                            {productVideo && (
                              <small className="text-muted d-block mt-1">
                                {productVideo.name} (
                                {(productVideo.size / 1024 / 1024).toFixed(2)}
                                MB)
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col lg={6}>
                      <div className="mb-3">
                        <Form.Label htmlFor="simple-category-select">
                          Category
                        </Form.Label>
                        <Select
                          isMulti
                          options={categoryOptions}
                          className="basic-multi-select"
                          classNamePrefix="select"
                          onChange={(selectedOptions) => {
                            const selectedIds = selectedOptions.map(
                              (opt) => opt.value
                            );
                            setSelectedCategoryIds(selectedIds);
                          }}
                          value={categoryOptions.filter((option) =>
                            selectedCategoryIds.includes(option.value)
                          )}
                        />
                        <Form.Text className="text-muted">
                          Hold Ctrl (or Cmd) to select multiple categories
                        </Form.Text>
                      </div>
                      <div className="mb-3">
                        <Form.Label htmlFor="simple-attribute-set-input">
                          Attribute Set (Optional)
                        </Form.Label>
                        <Form.Select
                          id="simple-attribute-set-input"
                          value={selectedSetId}
                          onChange={handleSetChange}
                        >
                          <option value="">
                            Select Attribute Set (Optional)
                          </option>
                          {attributeSets?.map((set) => (
                            <option key={set._id} value={set._id}>
                              {set.name}
                            </option>
                          ))}
                        </Form.Select>
                      </div>

                      <div className="mb-3 d-flex flex-wrap gap-3">
                        <Form.Check
                          type="switch"
                          id="taxable-switch"
                          label="Taxable"
                          checked={isTaxable}
                          onChange={(e) => setIsTaxable(e.target.checked)}
                        />
                        <Form.Check
                          type="switch"
                          id="new-arrival-switch"
                          label="New Arrival"
                          checked={isNewArrival}
                          onChange={(e) => setIsNewArrival(e.target.checked)}
                        />
                        <Form.Check
                          type="switch"
                          id="featured-switch"
                          label="Featured"
                          checked={isFeatured}
                          onChange={(e) => setIsFeatured(e.target.checked)}
                        />
                      </div>
                      <div className="mb-3">
                        <Form.Label htmlFor="simple-price-input">
                          Price (Optional - will override karat calculation)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          id="simple-price-input"
                          value={
                            typeof attributeValues["price"]?.[0] === "number"
                              ? attributeValues["price"]?.[0]
                              : ""
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value);
                            handleAttributeChange("price", value);

                            // Also update the isExplicit flag
                            setAttributeValues((prev) => ({
                              ...prev,
                              isExplicit: [value !== ""], // <-- wrap in an array
                            }));
                          }}
                          placeholder="Enter fixed price (overrides karat calculation)"
                          step="0.01"
                          min="0"
                        />
                        <Form.Text className="text-muted">
                          Leave empty to calculate price based on karat and
                          weight
                        </Form.Text>
                      </div>
                      <div className="mb-3">
                        <Form.Label htmlFor="simple-quantity-input">
                          Quantity (Optional)
                        </Form.Label>
                        <Form.Control
                          type="number"
                          id="simple-quantity-input"
                          value={
                            typeof attributeValues["quantity"]?.[0] === "number"
                              ? attributeValues["quantity"]?.[0]
                              : ""
                          }
                          onChange={(e) =>
                            handleAttributeChange(
                              "quantity",
                              Number(e.target.value)
                            )
                          }
                          placeholder="Enter product quantity"
                          min="0"
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              {selectedSetAttributes?.length > 0 && activeTab === "simple" && (
                <Card>
                  <Card.Header>
                    <div className="d-flex">
                      <div className="flex-shrink-0 me-3">
                        <div className="avatar-sm">
                          <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                            <i className="bi bi-list-ul"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="card-title mb-1">Product Attributes</h5>
                        <p className="text-muted mb-0">
                          Configure product attributes.
                        </p>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {selectedSetAttributes.map((attr) => (
                        <Col lg={6} key={attr._id}>
                          <div className="mb-3">
                            <Form.Label>
                              {attr.label} ({attr.type})
                              {attr.isCharge && (
                                <span className="text-danger">
                                  {" "}
                                  (Additional Charge)
                                </span>
                              )}
                            </Form.Label>

                            {attr.type === "string" ? (
                              <Form.Control
                                type="text"
                                value={getFormControlValue(
                                  attributeValues[attr.label]?.[0]
                                )}
                                onChange={(e) =>
                                  handleAttributeChange(
                                    attr.label,
                                    e.target.value
                                  )
                                }
                              />
                            ) : attr.type === "number" ? (
                              <>
                                <Form.Control
                                  type="number"
                                  value={getFormControlValue(
                                    attributeValues[attr.label]?.[0]
                                  )}
                                  onChange={(e) =>
                                    handleAttributeChange(
                                      attr.label,
                                      Number(e.target.value)
                                    )
                                  }
                                  step={attr.step || "any"}
                                />
                                {attr.isCharge && attr.isVariable && (
                                  <div className="mt-2">
                                    <Form.Label>Value Type</Form.Label>
                                    <Form.Select
                                      value={
                                        typeof attributeValues[
                                          `${attr.label}_valueType`
                                        ]?.[0] === "string" ||
                                        typeof attributeValues[
                                          `${attr.label}_valueType`
                                        ]?.[0] === "number"
                                          ? String(
                                              attributeValues[
                                                `${attr.label}_valueType`
                                              ]?.[0]
                                            )
                                          : "fixed"
                                      }
                                      onChange={(e) =>
                                        handleAttributeChange(
                                          `${attr.label}_valueType`,
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="fixed">
                                        Fixed Amount
                                      </option>
                                      <option value="percentage">
                                        Percentage
                                      </option>
                                    </Form.Select>
                                  </div>
                                )}
                              </>
                            ) : attr.type === "boolean" ? (
                              <Form.Check
                                type="switch"
                                checked={
                                  attributeValues[attr.label]?.[0] === true ||
                                  attributeValues[attr.label]?.[0] === "true"
                                }
                                onChange={(e) =>
                                  handleAttributeChange(
                                    attr.label,
                                    e.target.checked
                                  )
                                }
                              />
                            ) : attr.type === "options" ? (
                              <>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <Form.Select
                                    value={getFormControlValue(
                                      attributeValues[attr.label]?.[0]
                                    )}
                                    onChange={(e) =>
                                      handleAttributeChange(
                                        attr.label,
                                        e.target.value
                                      )
                                    }
                                  >
                                    <option value="">
                                      Select {attr.label}
                                    </option>
                                    {attr.options?.map((opt, i) => (
                                      <option key={i} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {attr.isCharge && attr.isVariable && (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => {
                                        const currentValues =
                                          attributeValues[attr.label] || [];
                                        setAttributeValues((prev) => ({
                                          ...prev,
                                          [attr.label]: [...currentValues, ""],
                                        }));
                                      }}
                                    >
                                      Add More
                                    </Button>
                                  )}
                                </div>

                                {/* Render all selected values for this attribute */}
                                {(attributeValues[attr.label] || []).map(
                                  (value, index) => (
                                    <div key={index} className="mb-2">
                                      {index > 0 && (
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                          <Form.Select
                                            value={getFormControlValue(value)}
                                            onChange={(e) => {
                                              const newValues = [
                                                ...(attributeValues[
                                                  attr.label
                                                ] || []),
                                              ];
                                              newValues[index] = e.target.value;
                                              setAttributeValues((prev) => ({
                                                ...prev,
                                                [attr.label]: newValues,
                                              }));
                                            }}
                                          >
                                            <option value="">
                                              Select {attr.label}
                                            </option>
                                            {attr.options?.map((opt, i) => (
                                              <option key={i} value={opt}>
                                                {opt}
                                              </option>
                                            ))}
                                          </Form.Select>
                                          <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => {
                                              const newValues = [
                                                ...(attributeValues[
                                                  attr.label
                                                ] || []),
                                              ];
                                              newValues.splice(index, 1);
                                              setAttributeValues((prev) => ({
                                                ...prev,
                                                [attr.label]: newValues,
                                              }));
                                            }}
                                          >
                                            Remove
                                          </Button>
                                        </div>
                                      )}

                                      {/* Add QTY and Price fields for chargeable variable options */}
                                      {attr.isCharge &&
                                        attr.isVariable &&
                                        value && (
                                          <div className="mt-2">
                                            <Row>
                                              <Col sm={6}>
                                                <Form.Label>QTY</Form.Label>
                                                <Form.Control
                                                  type="number"
                                                  min="0"
                                                  value={getSafeValue(
                                                    attributeValues[
                                                      `${attr.label}_qty_${index}`
                                                    ]?.[0]
                                                  )}
                                                  onChange={(e) =>
                                                    handleAttributeChange(
                                                      `${attr.label}_qty_${index}`,
                                                      Number(e.target.value)
                                                    )
                                                  }
                                                  placeholder="Quantity"
                                                />
                                              </Col>
                                              <Col sm={6}>
                                                <Form.Label>Price</Form.Label>
                                                <Form.Control
                                                  type="number"
                                                  min="0"
                                                  step="0.01"
                                                  value={getSafeValue(
                                                    attributeValues[
                                                      `${attr.label}_price_${index}`
                                                    ]?.[0]
                                                  )}
                                                  onChange={(e) =>
                                                    handleAttributeChange(
                                                      `${attr.label}_price_${index}`,
                                                      Number(e.target.value)
                                                    )
                                                  }
                                                  placeholder="Additional Price"
                                                />
                                              </Col>
                                            </Row>
                                          </div>
                                        )}
                                    </div>
                                  )
                                )}
                              </>
                            ) : null}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Submit button inside the form */}
              <div className="text-end mb-3">
                <Button
                  variant="success"
                  type="submit"
                  className="w-sm"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : isEditMode
                    ? "Update Product"
                    : "Create Product"}
                </Button>
              </div>
              {submitSuccess && (
                <div className="alert alert-success">
                  {submitSuccessMessage}
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xl={12} lg={8}>
                  <Card>
                    <Card.Header>
                      <div className="d-flex">
                        <div className="flex-shrink-0 me-3">
                          <div className="avatar-sm">
                            <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                              <i className="bi bi-box-seam"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1">
                          <h5 className="card-title mb-1">
                            Product Information
                          </h5>
                          <p className="text-muted mb-0">
                            Fill all information below.
                          </p>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Form.Label htmlFor="sku-input">
                              SKU (Optional)
                            </Form.Label>
                            <Form.Control
                              type="text"
                              id="sku-input"
                              value={sku}
                              onChange={(e) => setSku(e.target.value)}
                              placeholder="Enter product SKU"
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Form.Label htmlFor="name-input">
                              Product Name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              id="name-input"
                              value={productName}
                              onChange={(e) => setProductName(e.target.value)}
                              placeholder="Enter product name"
                              required
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={6}>
                          <div className="mb-3">
                            <Form.Label>Product Images(Max 5)</Form.Label>
                            <Dropzone
                              onDrop={(acceptedFiles: File[]) => {
                                if (acceptedFiles.length === 0) return; // prevent empty drops

                                if (
                                  existingProductImages.length +
                                    productImages.length +
                                    acceptedFiles.length <=
                                  5
                                ) {
                                  setProductImages([
                                    ...productImages,
                                    ...acceptedFiles,
                                  ]);
                                } else {
                                  toast?.error?.("Maximum 5 images allowed") ??
                                    console.error("Maximum 5 images allowed");
                                }
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <div className="dropzone dz-clickable text-center">
                                  <div
                                    className="dz-message needsclick"
                                    {...getRootProps()}
                                  >
                                    <div className="mb-3">
                                      <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                    </div>
                                    <h5>Drop files here or click to upload.</h5>
                                    <p className="text-muted">
                                      {existingProductImages.length +
                                        productImages.length}{" "}
                                      of 5 images uploaded
                                    </p>
                                  </div>
                                </div>
                              )}
                            </Dropzone>

                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {/* Display existing images from API */}
                              {existingProductImages.map((imageUrl, idx) => (
                                <div
                                  key={`existing-${idx}`}
                                  className="image-thumbnail-wrapper position-relative"
                                >
                                  <img
                                    src={imageUrl}
                                    alt={`Product ${idx + 1}`}
                                    className="image-thumbnail"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "2px solid #e9ecef",
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="image-remove-btn position-absolute"
                                    style={{
                                      top: "-5px",
                                      right: "-5px",
                                      width: "24px",
                                      height: "24px",
                                      borderRadius: "50%",
                                      padding: "0",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "14px",
                                      lineHeight: "1",
                                    }}
                                    onClick={() =>
                                      handleRemoveExistingProductImage(idx)
                                    }
                                  >
                                    ×
                                  </Button>
                                </div>
                              ))}

                              {/* New uploads */}
                              {productImages.map((file, idx) => (
                                <div
                                  key={`new-${idx}`}
                                  className="image-thumbnail-wrapper position-relative"
                                >
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`New upload ${idx + 1}`}
                                    className="image-thumbnail"
                                    style={{
                                      width: "100px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                      border: "2px solid #e9ecef",
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="image-remove-btn position-absolute"
                                    style={{
                                      top: "-5px",
                                      right: "-5px",
                                      width: "24px",
                                      height: "24px",
                                      borderRadius: "50%",
                                      padding: "0",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "14px",
                                      lineHeight: "1",
                                    }}
                                    onClick={() =>
                                      handleRemoveProductImage(idx)
                                    }
                                  >
                                    ×
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                          <style>{`
                            .image-thumbnail-wrapper {
                              position: relative;
                              display: inline-block;
                            }

                            .image-thumbnail {
                              width: 100px;
                              height: 100px;
                              object-fit: cover;
                              border-radius: 8px;
                              border: 2px solid #e9ecef;
                              transition: transform 0.2s ease;
                            }

                            .image-thumbnail:hover {
                              transform: scale(1.05);
                            }

                            .image-remove-btn {
                              top: -5px;
                              right: -5px;
                              width: 24px;
                              height: 24px;
                              border-radius: 50%;
                              padding: 0;
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-size: 14px;
                              line-height: 1;
                              z-index: 10;
                            }
                          `}</style>
                        </Col>

                        <Col lg={6}>
  <div className="mb-3">
    <Form.Label>Product Video (Optional)</Form.Label>
    <Dropzone
      onDrop={handleProductVideo}
      accept={{
        "video/*": [".mp4", ".mov", ".avi", ".mkv", ".webm"],
      }}
      maxFiles={1}
    >
      {({ getRootProps, getInputProps }) => (
        <div className="dropzone dz-clickable text-center">
          <div className="dz-message needsclick" {...getRootProps()}>
            <div className="mb-3">
              <i className="display-4 text-muted ri-video-line" />
            </div>
            <h5>Drop video file here or click to upload.</h5>
            <p className="text-muted">Max 50MB, one video allowed</p>
          </div>
        </div>
      )}
    </Dropzone>

    {/* Display current video */}
    {(productVideo || existingProductVideo) && (
      <div className="mt-3">
        <div className="video-preview-wrapper position-relative">
          <video
            controls
            src={
              productVideo
                ? URL.createObjectURL(productVideo)
                : existingProductVideo || ""
            }
            className="video-thumbnail"
            style={{
              width: "100%",
              maxHeight: "200px",
              borderRadius: "8px",
              border: "2px solid #e9ecef",
            }}
          />
          <Button
            variant="danger"
            size="sm"
            className="video-remove-btn position-absolute"
            style={{
              top: "-5px",
              right: "-5px",
              width: "24px",
              height: "24px",
              borderRadius: "50%",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              lineHeight: "1",
              zIndex: 10,
            }}
            onClick={handleRemoveProductVideo}
          >
            ×
          </Button>
        </div>
        {productVideo && (
          <small className="text-muted d-block mt-1">
            {productVideo.name} ({(productVideo.size / 1024 / 1024).toFixed(2)}
            MB)
          </small>
        )}
      </div>
    )}
  </div>
</Col>

                      
                        {/* <Col lg={6}>
                          
                        </Col> */}
                        <Col lg={6}>
                          <div className="mb-3">
                            <Form.Label htmlFor="category-select">
                              Category
                            </Form.Label>
                            <Select
                              isMulti
                              options={categoryOptions}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              onChange={(selectedOptions) => {
                                const selectedIds = selectedOptions.map(
                                  (opt) => opt.value
                                );
                                setSelectedCategoryIds(selectedIds);
                              }}
                              value={categoryOptions.filter((option) =>
                                selectedCategoryIds.includes(option.value)
                              )}
                            />
                          </div>
                          <div className="mb-3">
                            <Form.Label htmlFor="attribute-set-input">
                              Attribute Set
                            </Form.Label>
                            <Form.Select
                              id="attribute-set-input"
                              value={selectedSetId}
                              onChange={handleSetChange}
                              required
                            >
                              <option value="">Select Attribute Set</option>
                              {attributeSets?.map((set) => (
                                <option key={set._id} value={set._id}>
                                  {set.name}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className="mb-3 d-flex flex-wrap gap-3">
                            <Form.Check
                              type="switch"
                              id="taxable-switch"
                              label="Taxable"
                              checked={isTaxable}
                              onChange={(e) => setIsTaxable(e.target.checked)}
                            />
                            <Form.Check
                              type="switch"
                              id="new-arrival-switch"
                              label="New Arrival"
                              checked={isNewArrival}
                              onChange={(e) =>
                                setIsNewArrival(e.target.checked)
                              }
                            />
                            <Form.Check
                              type="switch"
                              id="featured-switch"
                              label="Featured"
                              checked={isFeatured}
                              onChange={(e) => setIsFeatured(e.target.checked)}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {selectedSetAttributes?.length > 0 && (
                    <Card>
                      <Card.Header>
                        <div className="d-flex">
                          <div className="flex-shrink-0 me-3">
                            <div className="avatar-sm">
                              <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                                <i className="bi bi-list-ul"></i>
                              </div>
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="card-title mb-1">
                              Product Attributes
                            </h5>
                            <p className="text-muted mb-0">
                              Configure product attributes.
                            </p>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          {selectedSetAttributes
                            ?.filter((attr) => !attr.isVariable)
                            ?.map((attr) => (
                              <Col lg={6} key={attr._id}>
                                <div className="mb-3">
                                  <Form.Label>
                                    {attr.label} ({attr.type})
                                  </Form.Label>
                                  {attr.type === "string" ? (
                                    <Form.Control
                                      type="text"
                                      value={getFormControlValue(
                                        attributeValues[attr._id]?.[0]
                                      )}
                                      onChange={(e) =>
                                        handleAttributeChange(
                                          attr._id,
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : attr.type === "number" ? (
                                    <Form.Control
                                      type="number"
                                      value={getFormControlValue(
                                        attributeValues[attr._id]?.[0]
                                      )}
                                      onChange={(e) =>
                                        handleAttributeChange(
                                          attr._id,
                                          Number(e.target.value)
                                        )
                                      }
                                      step={attr.step || "any"}
                                    />
                                  ) : attr.type === "boolean" ? (
                                    <Form.Check
                                      type="switch"
                                      id={`${attr._id}-switch`}
                                      label=""
                                      checked={
                                        attributeValues[attr._id]?.[0] ===
                                          "true" || false
                                      }
                                      onChange={(e) =>
                                        handleAttributeChange(
                                          attr._id,
                                          e.target.checked.toString()
                                        )
                                      }
                                    />
                                  ) : attr.type === "options" ? (
                                    <div>
                                      {attr.options?.map((opt, i) => (
                                        <Form.Check
                                          key={i}
                                          type="checkbox"
                                          id={`${attr._id}-${i}`}
                                          label={opt}
                                          checked={(
                                            attributeValues[attr._id] || []
                                          ).includes(opt)}
                                          onChange={() =>
                                            handleMultiSelect(attr._id, opt)
                                          }
                                          className="mb-2"
                                        />
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              </Col>
                            ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  )}
                  {variantPrices?.length > 0 && (
                    <Card>
                      <Card.Header>
                        <div className="d-flex">
                          <div className="flex-shrink-0 me-3">
                            <div className="avatar-sm">
                              <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                                <i className="bi bi-collection"></i>
                              </div>
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="card-title mb-1">
                              Product Variants
                            </h5>
                            <p className="text-muted mb-0">
                              Configure product variants.
                            </p>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        {variantPrices.map((variant, i) => {
                          const combo = Array.isArray(variant.combo)
                            ? variant.combo
                            : [];
                          const karatObj = combo.find(
                            (c) => c.label.toLowerCase() === "karat"
                          );
                          const karatValue = karatObj?.value;

                          // Get variable attributes for this variant
                          const variableAttributes =
                            selectedSetAttributes?.filter(
                              (attr) =>
                                attr.isVariable && attr.type === "options"
                            ) || [];

                          return (
                            <Card key={i} className="mb-4">
                              <Card.Body>
                                <Card.Title>
                                  Variant {i + 1}:{" "}
                                  {combo.map((c, idx) => (
                                    <span key={idx}>
                                      {c.label}: {c.value}
                                      {idx < combo.length - 1 ? ", " : ""}
                                    </span>
                                  ))}
                                </Card.Title>

                                <Row>
                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <Form.Label>Quantity</Form.Label>
                                      <Form.Control
                                        type="number"
                                        value={variant.quantity || 0}
                                        onChange={(e) => {
                                          const updated = [...variantPrices];
                                          updated[i].quantity =
                                            parseInt(e.target.value) || 0;
                                          setVariantPrices(updated);
                                        }}
                                        min="0"
                                      />
                                    </div>
                                    <div className="mb-3">
                                      <Form.Label>Price</Form.Label>
                                      <div className="d-flex align-items-center">
                                        <span className="me-2">
                                          ₹{variant.price || 0}
                                        </span>
                                        {karatValue &&
                                          typeof karatValue === "string" && (
                                            <small className="text-muted">
                                              (Base: ₹{currentBasePrice} ×{" "}
                                              {karatMap[karatValue] || 0}%)
                                            </small>
                                          )}
                                      </div>
                                    </div>
                                    {/* Variable Options (for option-type attributes) */}
                                    {variableAttributes.length > 0 && (
                                      <div className="mb-3">
                                        <Form.Label>
                                          Variable Options
                                        </Form.Label>
                                        {variableAttributes.map((attr) => {
                                          // Get existing options from variant.attributes or initialize empty array
                                          const existingOptions = (
                                            variant.attributes || []
                                          )
                                            .filter(
                                              (a: VariantAttribute) =>
                                                a.label === attr.label &&
                                                a.isVariable &&
                                                a.type === "options"
                                            )
                                            .map((a: VariantAttribute) => ({
                                              selectedOption:
                                                a.value.toString(),
                                              inputValue: a.QTY || 0,
                                              type: "number" as const,
                                              price: a.price || 0,
                                              QTY: a.QTY || 0,
                                            }));

                                          // Use either the state options or existing options from attributes
                                          const currentOptions =
                                            variant.variableOptions?.[
                                              attr._id
                                            ] || existingOptions;

                                          return (
                                            <div
                                              key={attr._id}
                                              className="mb-4"
                                            >
                                              <h6>
                                                {attr.label}
                                                {attr.isCharge && (
                                                  <span className="badge bg-success ms-2">
                                                    Chargeable
                                                  </span>
                                                )}
                                              </h6>

                                              {currentOptions.map(
                                                (
                                                  option: VariableOptionValue,
                                                  valueIndex: number
                                                ) => {
                                                  const selectedOptions =
                                                    currentOptions.map(
                                                      (
                                                        opt: VariableOptionValue
                                                      ) => opt.selectedOption
                                                    );
                                                  const availableOptions =
                                                    attr.options.filter(
                                                      (opt: string) =>
                                                        !selectedOptions.includes(
                                                          opt
                                                        ) ||
                                                        opt ===
                                                          option.selectedOption
                                                    );

                                                  return (
                                                    <div
                                                      key={valueIndex}
                                                      className="card p-3 mb-3"
                                                    >
                                                      <div className="row g-3 align-items-center">
                                                        {/* Option Value Dropdown */}
                                                        <div className="col-md-3">
                                                          <Form.Label>
                                                            Option Value
                                                          </Form.Label>
                                                          <Form.Select
                                                            value={
                                                              option.selectedOption
                                                            }
                                                            onChange={(e) =>
                                                              handleVariableOptionChange(
                                                                i,
                                                                attr._id,
                                                                valueIndex,
                                                                e.target.value
                                                              )
                                                            }
                                                          >
                                                            <option value="">
                                                              Select{" "}
                                                              {attr.label}
                                                            </option>
                                                            {availableOptions.map(
                                                              (
                                                                opt: string,
                                                                idx: number
                                                              ) => (
                                                                <option
                                                                  key={idx}
                                                                  value={opt}
                                                                >
                                                                  {opt}
                                                                </option>
                                                              )
                                                            )}
                                                          </Form.Select>
                                                        </div>

                                                        {/* QTY Field */}
                                                        <div className="col-md-3">
                                                          <Form.Label>
                                                            QTY
                                                          </Form.Label>
                                                          <Form.Control
                                                            type="number"
                                                            value={
                                                              option.inputValue
                                                            }
                                                            onChange={(e) => {
                                                              const newValue =
                                                                parseFloat(
                                                                  e.target.value
                                                                ) || 0;
                                                              setVariantPrices(
                                                                (
                                                                  prevPrices
                                                                ) => {
                                                                  const updated =
                                                                    [
                                                                      ...prevPrices,
                                                                    ];
                                                                  if (
                                                                    !updated[i]
                                                                      .variableOptions
                                                                  ) {
                                                                    updated[
                                                                      i
                                                                    ].variableOptions =
                                                                      {};
                                                                  }
                                                                  if (
                                                                    !updated[i]
                                                                      .variableOptions![
                                                                      attr._id
                                                                    ]
                                                                  ) {
                                                                    updated[
                                                                      i
                                                                    ].variableOptions![
                                                                      attr._id
                                                                    ] = [];
                                                                  }
                                                                  updated[
                                                                    i
                                                                  ].variableOptions![
                                                                    attr._id
                                                                  ][
                                                                    valueIndex
                                                                  ] = {
                                                                    ...updated[
                                                                      i
                                                                    ]
                                                                      .variableOptions![
                                                                      attr._id
                                                                    ][
                                                                      valueIndex
                                                                    ],
                                                                    inputValue:
                                                                      newValue,
                                                                    QTY: newValue,
                                                                  };
                                                                  return updated;
                                                                }
                                                              );
                                                            }}
                                                          />
                                                        </div>

                                                        {/* Price Field */}
                                                        {attr.isCharge && (
                                                          <div className="col-md-3">
                                                            <Form.Label>
                                                              Price
                                                            </Form.Label>
                                                            <Form.Control
                                                              type="number"
                                                              value={
                                                                option.price
                                                              }
                                                              onChange={(e) =>
                                                                handleVariableOptionPriceChange(
                                                                  i,
                                                                  attr._id,
                                                                  valueIndex,
                                                                  parseFloat(
                                                                    e.target
                                                                      .value
                                                                  ) || 0
                                                                )
                                                              }
                                                            />
                                                          </div>
                                                        )}

                                                        {/* Remove Button */}
                                                        <div
                                                          className={`col-md-${
                                                            attr.isCharge
                                                              ? "3"
                                                              : "3"
                                                          }`}
                                                        >
                                                          <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() =>
                                                              handleRemoveVariableOption(
                                                                i,
                                                                attr._id,
                                                                valueIndex
                                                              )
                                                            }
                                                            className="w-100 mt-2 mt-md-0"
                                                          >
                                                            Remove
                                                          </Button>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  );
                                                }
                                              )}

                                              {/* Add more button */}
                                              {currentOptions.length <
                                                attr.options.length && (
                                                <Button
                                                  variant="outline-primary"
                                                  size="sm"
                                                  onClick={() => {
                                                    const firstAvailable =
                                                      attr.options.find(
                                                        (opt: string) =>
                                                          !currentOptions.some(
                                                            (
                                                              o: VariableOptionValue
                                                            ) =>
                                                              o.selectedOption ===
                                                              opt
                                                          )
                                                      );
                                                    if (firstAvailable) {
                                                      handleVariableOptionChange(
                                                        i,
                                                        attr._id,
                                                        currentOptions.length,
                                                        firstAvailable
                                                      );
                                                    }
                                                  }}
                                                  className="mt-1"
                                                >
                                                  + Add More {attr.label}
                                                </Button>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    {/* Other Variable Attributes (non-option type) */}
                                    {/* Other Variable Attributes (non-option type) */}
                                    {/* Other Variable Attributes (non-option type) */}
                                    {selectedSetAttributes
                                      ?.filter(
                                        (attr) =>
                                          attr.isVariable &&
                                          attr.type !== "options" &&
                                          !selectedSetAttributes.some(
                                            (a) =>
                                              a.type === "options" &&
                                              !a.isVariable &&
                                              a.label === attr.label
                                          )
                                      )
                                      ?.map((attr) => {
                                        const currentValue =
                                          variant.variableValues?.[attr._id]
                                            ?.value ?? "";
                                        const currentValueType =
                                          variant.variableValues?.[attr._id]
                                            ?.valueType ?? "fixed";
                                        const currentShowStatus =
                                          variant.variableValues?.[attr._id]
                                            ?.show ?? true;

                                        return (
                                          <div key={attr._id} className="mb-3">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                              <div className="d-flex align-items-center">
                                                <Form.Label className="mb-0">
                                                  {attr.label}
                                                  {attr.isCharge && (
                                                    <span className="badge bg-success ms-2">
                                                      Chargeable
                                                    </span>
                                                  )}
                                                </Form.Label>
                                              </div>
                                              <div className="form-check form-switch">
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  id={`toggle-${attr._id}`}
                                                  checked={currentShowStatus}
                                                  onChange={(e) => {
                                                    const updated = [
                                                      ...variantPrices,
                                                    ];
                                                    if (
                                                      !updated[i].variableValues
                                                    ) {
                                                      updated[
                                                        i
                                                      ].variableValues = {};
                                                    }
                                                    updated[i].variableValues[
                                                      attr._id
                                                    ] = {
                                                      ...updated[i]
                                                        .variableValues[
                                                        attr._id
                                                      ],
                                                      show: e.target.checked,
                                                    };
                                                    setVariantPrices(updated);
                                                  }}
                                                />
                                                <label
                                                  className="form-check-label"
                                                  htmlFor={`toggle-${attr._id}`}
                                                >
                                                  Show
                                                </label>
                                              </div>
                                            </div>

                                            <div className="d-flex">
                                              <Form.Control
                                                type={
                                                  attr.type === "number"
                                                    ? "number"
                                                    : "text"
                                                }
                                                value={currentValue.toString()}
                                                onChange={(e) => {
                                                  const updated = [
                                                    ...variantPrices,
                                                  ];
                                                  if (
                                                    !updated[i].variableValues
                                                  ) {
                                                    updated[i].variableValues =
                                                      {};
                                                  }
                                                  updated[i].variableValues[
                                                    attr._id
                                                  ] = {
                                                    ...updated[i]
                                                      .variableValues[attr._id],
                                                    value:
                                                      attr.type === "number"
                                                        ? Number(e.target.value)
                                                        : e.target.value,
                                                  };
                                                  setVariantPrices(updated);
                                                }}
                                              />
                                              {/* Only show value type dropdown for numeric chargeable attributes */}
                                              {attr.isCharge &&
                                                attr.type === "number" && (
                                                  <Form.Select
                                                    value={currentValueType}
                                                    onChange={(e) => {
                                                      const updated = [
                                                        ...variantPrices,
                                                      ];
                                                      if (
                                                        !updated[i]
                                                          .variableValues
                                                      ) {
                                                        updated[
                                                          i
                                                        ].variableValues = {};
                                                      }
                                                      updated[i].variableValues[
                                                        attr._id
                                                      ] = {
                                                        ...updated[i]
                                                          .variableValues[
                                                          attr._id
                                                        ],
                                                        valueType:
                                                          e.target.value,
                                                      };
                                                      setVariantPrices(updated);
                                                    }}
                                                    style={{
                                                      width: "120px",
                                                      marginLeft: "10px",
                                                    }}
                                                  >
                                                    <option value="fixed">
                                                      Fixed ₹
                                                    </option>
                                                    <option value="percentage">
                                                      Percentage %
                                                    </option>
                                                  </Form.Select>
                                                )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                  </Col>

                                  <Col lg={6}>
                                    <div className="mb-3">
                                      <Form.Label>Images (max 5)</Form.Label>
                                      <Dropzone
                                        onDrop={(acceptedFiles) =>
                                          handleVariantImages(i, acceptedFiles)
                                        }
                                        accept={{
                                          "image/*": [
                                            ".jpeg",
                                            ".jpg",
                                            ".png",
                                            ".gif",
                                          ],
                                        }}
                                        maxFiles={
                                          5 - (variantImages[i]?.length || 0)
                                        }
                                      >
                                        {({ getRootProps, getInputProps }) => (
                                          <div className="dropzone dz-clickable text-center">
                                            <div
                                              className="dz-message needsclick"
                                              {...getRootProps()}
                                            >
                                              <div className="mb-3">
                                                <i className="display-4 text-muted ri-upload-cloud-2-fill" />
                                              </div>
                                              <h5>
                                                Drop files here or click to
                                                upload.
                                              </h5>
                                            </div>
                                          </div>
                                        )}
                                      </Dropzone>
                                    </div>

                                    <div className="d-flex flex-wrap gap-2">
                                      {variantImages[i]
                                        ?.filter(
                                          (img) => !img.markedForDeletion
                                        )
                                        .map((img, imgIdx) => (
                                          <div
                                            key={img.id} // Use the stable ID
                                            style={{
                                              width: "80px",
                                              height: "80px",
                                              overflow: "hidden",
                                              borderRadius: "4px",
                                              position: "relative",
                                            }}
                                          >
                                            <img
                                              src={img.path}
                                              alt={`Variant ${i + 1}`}
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                              }}
                                            />
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              style={{
                                                position: "absolute",
                                                top: "2px",
                                                right: "2px",
                                                padding: "0.15rem 0.3rem",
                                              }}
                                              onClick={() =>
                                                handleRemoveVariantImage(
                                                  i,
                                                  imgIdx
                                                )
                                              }
                                            >
                                              ×
                                            </Button>
                                          </div>
                                        ))}
                                    </div>

                                    {/* VIDEO SECTION - Add this inside the variant mapping */}
<div className="mb-3">
  <Form.Label>Variant Video (Optional)</Form.Label>
  <Dropzone
    onDrop={(acceptedFiles) => handleVariantVideo(i, acceptedFiles)}
    accept={{
      "video/*": [".mp4", ".mov", ".avi", ".mkv", ".webm"],
    }}
    maxFiles={1}
  >
    {({ getRootProps, getInputProps }) => (
      <div className="dropzone dz-clickable text-center">
        <div className="dz-message needsclick" {...getRootProps()}>
          <div className="mb-3">
            <i className="display-4 text-muted ri-video-line" />
          </div>
          <h5>Drop video file here or click to upload.</h5>
          <p className="text-muted">Max 50MB, one video allowed</p>
        </div>
      </div>
    )}
  </Dropzone>

  {/* Display current video */}
  {(variantVideos[i] || existingVariantVideos[i]) && (
    <div className="mt-3">
      <div className="video-preview-wrapper position-relative">
        <video
          controls
          src={
            variantVideos[i]
              ? URL.createObjectURL(variantVideos[i]!)
              : existingVariantVideos[i] || ""
          }
          className="video-thumbnail"
          style={{
            width: "100%",
            maxHeight: "150px",
            borderRadius: "8px",
            border: "2px solid #e9ecef",
          }}
        />
        <Button
          variant="danger"
          size="sm"
          className="video-remove-btn position-absolute"
          style={{
            top: "-5px",
            right: "-5px",
            width: "24px",
            height: "24px",
            borderRadius: "50%",
            padding: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            lineHeight: "1",
            zIndex: 10,
          }}
          onClick={() => handleRemoveVariantVideo(i)}
        >
          ×
        </Button>
      </div>
      {variantVideos[i] && (
        <small className="text-muted d-block mt-1">
          {variantVideos[i]!.name} (
          {((variantVideos[i]!.size || 0) / 1024 / 1024).toFixed(2)}
          MB)
        </small>
      )}
    </div>
  )}
</div>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          );
                        })}
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              </Row>
              <div className="text-end mb-3">
                <Button
                  variant="success"
                  type="submit"
                  className="w-sm"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : isEditMode
                    ? "Update Product"
                    : "Create Product"}
                </Button>
              </div>
              {submitSuccess && (
                <div className="alert alert-success">
                  {submitSuccessMessage}
                </div>
              )}
            </form>
          )}
          {/* <div className="text-end mb-3">
            <Button
              variant="success"
              type="submit"
              className="w-sm"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Create Product"}
            </Button>
          </div> */}
          <Modal
            show={showImportModal}
            onHide={() => setShowImportModal(false)}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Import Products</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3">
                <Form.Label>Select CSV File</Form.Label>
                <Form.Control
                  type="file"
                  accept=".csv"
                  onChange={handleImportFileChange}
                  disabled={isImporting}
                />
                <div className="small text-muted mt-1">
                  CSV should include product data with headers matching the
                  export format.
                </div>
              </div>

              {isImporting && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Importing...</span>
                    <span>{importProgress}%</span>
                  </div>
                  <ProgressBar now={importProgress} />
                </div>
              )}

              {importResult && (
                <div
                  className={`alert alert-${
                    importResult.success ? "success" : "danger"
                  }`}
                >
                  {importResult.message}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2">
                      <h6>Errors:</h6>
                      <ul className="small">
                        {importResult.errors.map((error, idx) => (
                          <li key={idx}>
                            Row {error.row}: {error.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                disabled={isImporting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleImportProducts}
                disabled={!importFile || isImporting}
              >
                {isImporting ? "Importing..." : "Import"}
              </Button>
            </Modal.Footer>
          </Modal>

          {submitSuccess && (
            <div className="alert alert-success">{submitSuccessMessage}</div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
}

export default CreateProduct;
